// A performance group's measures (J-202; SPEC §7.3 Screen 2), at /groups/<groupId>?measure=<measureKey>,
// opened from Performance's group view. The group in the URL is its numeric id; `measure` names the
// measure whose patients are listed, by `measureKey` — never a patient value (phi.md rule 2).
// Holders of `acoQuality.read` only, as Performance; the patient list also needs `acoPatients.read`.
// J-260 (D8): the patient list's status opens the "why met / not met" pop-up for a holder of
// `acoPatients.read` with PHI access; J-301 (QB4 item 3b): a group user's for its own group's patients.
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { uiDocumentSchema } from "@vbltc/schema";

import { GroupMeasuresScreen } from "@/features/aco-groups/components/group-measures-screen";
import { uiConfigQueryOptions } from "@/features/aco-shell/api/ui-config";
import type { PatientWhyTriggerProps } from "@/features/aco-groups/components/group-patient-list";
import { WhyButton } from "@/features/aco-patients/components/why-button";
import { meAccessQueryOptions } from "@/features/auth/api/me-access";
import { usePermission } from "@/features/auth/use-permission";
import { usePhiAccess } from "@/features/auth/use-phi-access";
import { groupSearchSchema } from "@/lib/route-search";

export const Route = createFileRoute("/_authenticated/groups/$groupId")({
  params: {
    parse: (raw) => ({ groupId: z.coerce.number().int().positive().parse(raw.groupId) }),
    stringify: (params) => ({ groupId: String(params.groupId) }),
  },
  validateSearch: groupSearchSchema,
  // The shell draws the permission state for a role or account the group's reads refuse (J-209).
  staticData: {
    screenName: "Performance group",
    requires: { acoQuality: ["read"] },
    ownScope: true,
  },
  component: GroupPage,
});

function GroupPage() {
  const { groupId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const canReadPatients = usePermission({ acoPatients: ["read"] });
  const phiAccess = usePhiAccess();
  const access = useQuery(meAccessQueryOptions());
  // J-301 (QB4 item 3b): a group user too, for its group's patients (the API refuses any other).
  const canOpenWhy = canReadPatients && phiAccess && access.data !== undefined;
  const canOpenData = usePermission({ acoData: ["load"] });
  // J-274: the Inactive reason's configured words, for the patient list.
  const ui = useQuery(uiConfigQueryOptions());
  const document = uiDocumentSchema.safeParse(ui.data?.document);
  return (
    <GroupMeasuresScreen
      groupId={groupId}
      measureKey={search.measure}
      canReadPatients={canReadPatients}
      whyTrigger={
        canOpenWhy
          ? (row: PatientWhyTriggerProps) => <WhyButton {...row} canOpenData={canOpenData} />
          : undefined
      }
      inactiveWords={document.success ? document.data.text : undefined}
      onMeasureChange={(measure) => void navigate({ search: { ...search, measure } })}
      // J-268: the ACO filter of a group whose practices span ACOs; omitted is "Both".
      groupAco={search.groupAco}
      onGroupAcoChange={(groupAco) => void navigate({ search: { ...search, groupAco } })}
    />
  );
}
